import { useState } from 'react';
import { Button, Stack, Table, FileInput, Group, Alert } from '@mantine/core';
import { IconUpload, IconTrash } from '@tabler/icons-react';
import { type CompanyDocument } from 'src/company/types';
import { useCompanyDocuments } from 'src/company/hooks/useCompanyDocuments';
import { useCreateCompanyDocument } from 'src/company/hooks/useCreateCompanyDocument';
import { useDeleteCompanyDocument } from 'src/company/hooks/useDeleteCompanyDocument';
import { ExampleStack, type Example } from '../';

function apiHooksDemo(): Example {
  return {
    description: 'Upload, list, and delete company documents using the API hooks',
    render: <CompanyDocumentsDemo />,
  };
}

function rawJsonDemo(): Example {
  return {
    description: 'Raw JSON response from useCompanyDocuments hook',
    render: <RawJsonDemo />,
  };
}

export default function CompanyDocumentsSandbox() {
  return <ExampleStack examples={[apiHooksDemo(), rawJsonDemo()]} />;
}

function CompanyDocumentsDemo() {
  const [file, setFile] = useState<File | null>(null);
  const { documents, isLoading, refetch, error } = useCompanyDocuments();
  const { createCompanyDocument, isLoading: isCreating } = useCreateCompanyDocument();
  const { deleteCompanyDocument, isLoading: isDeleting } = useDeleteCompanyDocument();

  const handleUpload = async () => {
    if (!file) return;
    await createCompanyDocument(file);
    setFile(null);
    refetch();
  };

  const handleDelete = async (id: string) => {
    await deleteCompanyDocument(id);
    refetch();
  };

  if (error) {
    return <Alert color="red">Error loading documents: {String(error)}</Alert>;
  }

  return (
    <Stack>
      <Group>
        <FileInput
          placeholder="Select file"
          value={file}
          onChange={setFile}
          leftSection={<IconUpload size={16} />}
        />
        <Button onClick={handleUpload} loading={isCreating} disabled={!file}>
          Upload
        </Button>
      </Group>

      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>ID</Table.Th>
            <Table.Th>File Name</Table.Th>
            <Table.Th>File ID</Table.Th>
            <Table.Th>Size</Table.Th>
            <Table.Th>Type</Table.Th>
            <Table.Th>Created</Table.Th>
            <Table.Th>Actions</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          <DocumentRows
            documents={documents}
            isLoading={isLoading}
            isDeleting={isDeleting}
            handleDelete={handleDelete}
          />
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

function RawJsonDemo() {
  const { documents, isLoading, error } = useCompanyDocuments();

  if (isLoading) return <pre>Loading...</pre>;
  if (error) return <pre>Error: {String(error)}</pre>;

  return <pre style={{ margin: 0 }}>{JSON.stringify(documents, null, 2)}</pre>;
}

type Props = {
  documents: CompanyDocument[];
  isLoading: boolean;
  isDeleting: boolean;
  handleDelete: (id: string) => Promise<void>;
};

function DocumentRows({ documents, isLoading, isDeleting, handleDelete }: Props) {
  if (isLoading) {
    return (
      <Table.Tr>
        <Table.Td colSpan={7}>Loading...</Table.Td>
      </Table.Tr>
    );
  }

  if (documents.length === 0) {
    return (
      <Table.Tr>
        <Table.Td colSpan={7}>No documents</Table.Td>
      </Table.Tr>
    );
  }

  return documents.map(doc => (
    <Table.Tr key={doc.id}>
      <Table.Td>{doc.id}</Table.Td>
      <Table.Td>{doc.file.fileName}</Table.Td>
      <Table.Td>{doc.file.id}</Table.Td>
      <Table.Td>{doc.file.fileSize}</Table.Td>
      <Table.Td>{doc.file.fileType}</Table.Td>
      <Table.Td>{doc.createdAt}</Table.Td>
      <Table.Td>
        <Button
          variant="subtle"
          color="red"
          size="xs"
          leftSection={<IconTrash size={14} />}
          onClick={() => handleDelete(doc.id)}
          loading={isDeleting}
        >
          Delete
        </Button>
      </Table.Td>
    </Table.Tr>
  ));
}
